import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { clsx } from "clsx";
import { startCase } from "lodash";
import dynamic from "next/dynamic";
import { useParams, usePathname, useRouter } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { ProductImage } from "~/bff/types/ProductImage";
import { CombinedLink } from "~/components/combined-link/component";
import { HeartIcon } from "~/components/heart-icon/component";
import { Image } from "~/components/image/component";
import {
  ClickAndCollectTags,
  getClickAndCollectTagsForAllVariants,
} from "~/components/product-card/helpers";
import { ProductCardLink } from "~/components/product-card-link/component";
import { ProductVariant } from "~/components/sales-panel/types";
import { SHOPPING_LIST_LAYOUT } from "~/components/shopping-list-page/constants";
import { SizeSelectorProps } from "~/components/size-selector/component";
import { Tag } from "~/components/tag/component";
import { Keys } from "~/constants/common";
import { getCurrencyCodeByLocale } from "~/constants/i18n";
import { oneSizeCodesSet } from "~/constants/one-size-codes";
import {
  AvailabilityStatusData,
  AvailabilityStatusKey,
  clickAndCollectStatusData,
  ClickAndCollectStatusKey as clickAndCollectAvailabilityStatusKey,
  SalesChannelKey,
} from "~/constants/product-availability-statuses";
import { Variables } from "~/constants/request";
import { AuthContext } from "~/context/auth/context";
import { DictionaryLabelContext } from "~/context/dictionary-label/context";
import { DictionaryKeys } from "~/context/dictionary-label/types";
import { NotificationContext } from "~/context/notification/context";
import { ShoppingBagContext } from "~/context/shopping-bag/context";
import { ShoppingListContext } from "~/context/shopping-list/context";
import { ShoppingListPageContext } from "~/context/shopping-list-page/context";
import { useVariable } from "~/context/variables/hooks/use-variable";
import { setIndexToLocalStorage } from "~/helpers/ga-storage-handlers-for-product-index/index";
import {
  getDisplayPrice,
  getDisplayPriceFromVariants,
  getDisplayPriceWithClearanceFromVariants,
  getDisplayPriceWithoutCurrency,
} from "~/helpers/get-display-price";
import { getMainCategorySlugFromProductData } from "~/helpers/get-page-categories-titles";
import { getProductLinkBySlug } from "~/helpers/get-product-link-from-slug";
import { importComponentByName } from "~/helpers/import-component-by-name";
import { logBloomreachEvent } from "~/helpers/log-bloomreach-event";
import {
  BloomreachEventType,
  BloomreachGroupType,
} from "~/helpers/log-bloomreach-event/types";
import { prepareTextAttribute } from "~/helpers/prepare-text-attribute";
import { pushDataLayer } from "~/helpers/push-data-layer";
import { getSelectedOrPreferredStoreId } from "~/helpers/selected-store/get-selected-or-preferred-store-id";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { triggerAddToCartDYEvent } from "~/helpers/trigger-dy-event";
import { persistScrollPosition } from "~/hooks/anchoring/helpers";
import { useBreakpoints } from "~/hooks/use-break-points";
import { usePdpStockAvailsCampaign } from "~/hooks/use-pdp-stock-avails-campaign";
import { useProductComingSoonTag } from "~/hooks/use-product-coming-soon-tag/hook";
import { useProductPricePerUnitText } from "~/hooks/use-product-price-per-unit-text";
import { useProductTags } from "~/hooks/use-product-tags";
import { useSwapCCAndStoreDyCampaignData } from "~/hooks/use-swap-cc-and-store-dy-campaign-data";
import { Evergage } from "~/lib/salesforce/evergage";
import {
  EvergageActions,
  EvergageItemActions,
} from "~/lib/salesforce/evergage/actions";
import { useAzureConfigurator } from "~/services/azure-configurator/use-azure-configurator";
import { CcWhite24 } from "~/theme/icons/components/cc";
import { Trash } from "~/theme/icons/components/trash";

import { PricePanel } from "../price-panel/component";

import {
  PRODUCT_CARD_CLASSES,
  REMOVED_PRODUCT_CARD_CLASSES,
  imageTemplates,
} from "./constants";
import {
  getDataForGAAddToBag,
  getDataForGAClicks,
} from "./helpers/get-data-for-google-analytics";
import { ProductCardContainer, RemovedProductCardContainer } from "./styled";
import { ExtendedVariant, ProductCardProps, StatusConfig } from "./types";

const SizeSelector = dynamic<SizeSelectorProps>(async () =>
  importComponentByName(
    import("~/components/size-selector/component"),
    "SizeSelector",
  ),
);

const RemindButton = dynamic(async () =>
  importComponentByName(
    import("~/components/remind-button/component"),
    "RemindButton",
  ),
);

export const moveItemToShoppingBagNotification = "Item moved to shopping bag";
export const moveToShoppingBagText = "Move to Bag";

export const clickAndCollectStatuses = {
  [clickAndCollectAvailabilityStatusKey.availableInStore]:
    "In stock for Click + Collect",
  [clickAndCollectAvailabilityStatusKey.soldOut]: "Sold out for Click + Collect",
  [clickAndCollectAvailabilityStatusKey.notAvailableInStore]:
    "No Click + Collect trial in this store",
};

const DEFAULT_INVENTORY = {
  clickAndCollectStore: null,
  clickAndCollectStock: null,
  clickAndCollectAvailableQuantity: null,
  salesChannel: null,
  trial: false,
};

export const ProductCard = (props: ProductCardProps): React.ReactElement => {
  const {
    className,
    product,
    onClick,
    layout = "",
    sizeIsSelected,
    position = 1,
    variant,
    breadcrumbs,
    storeSelectorData,
    productToPageMap,
    restorationRef,
    widgetData,
    isRemindModal = false,
    isRecentlyViewed = false,
  } = props;
  const { locale } = useParams<GlobalPathParams>();
  const pathname = usePathname();
  const router = useRouter();
  const dictionaryLabel = useContext(DictionaryLabelContext);
  const config = useAzureConfigurator(locale);
  const isPricePerUnitFeatureEnabled = config?.featureFlags?.pricePerUnit?.enabled;
  const isClickAndCollectEnabled = config?.featureFlags?.clickAndCollect?.enabled;
  const isClearancePriceEnabled = config?.featureFlags?.clearancePricing?.enabled;
  const comingSoonPhase2 = config.featureFlags?.comingSoonPhase2?.enabled;
  const authContext = useContext(AuthContext);
  const productVariants = (product?.variants ?? []) as ClickAndCollectTags[];
  const productTags = useProductTags({
    data: {
      ...product,
      ...getClickAndCollectTagsForAllVariants([
        product as ClickAndCollectTags,
        ...productVariants,
      ]),
    },
    maximumTag: 2,
  });
  const comingSoonTag = useProductComingSoonTag();

  let pageNumber: number | undefined = 1;
  if (product?.productCode) {
    pageNumber = productToPageMap?.get(product.productCode);
  }

  const productRef = useRef<HTMLDivElement>(undefined);
  const removedProductRef = useRef<HTMLDivElement>(undefined);

  const {
    userShoppingListId,
    handleRemoveProductFromShoppingList,
    handleReplaceShoppingListItem,
    handleAddProductToDeleted,
    handleRemoveProductFromDeleted,
    handleUndoRemoveProductFromShopingList,
    deletedProducts,
    handleMoveItemFromShoppingListToShoppingBag,
  } = useContext(ShoppingListContext) ?? {};

  const { userShoppingBag, shoppingBagSkuMap, handleSetUserShoppingBag } =
    useContext(ShoppingBagContext) ?? {};

  const { translations, stockStatuses, isStoreSelected, sharedShoppingList } =
    useContext(ShoppingListPageContext) ?? {};

  const {
    selectSizeTextField,
    undoButtonName,
    theProductHasBeenRemovedTextField,
    sizeIsNeededForStockStatusTextField,
    shoppingBagLimitErrorText,
  } = translations ?? {};

  const statusConfig: StatusConfig = {
    [AvailabilityStatusKey.availableInStore]: stockStatuses?.inStock,
    [AvailabilityStatusKey.lowInStore]: stockStatuses?.stockIsRunning,
    [AvailabilityStatusKey.soldOutInStore]: stockStatuses?.soldOut,
    [AvailabilityStatusKey.notAvailableInStore]: stockStatuses?.notAvailable,
    [AvailabilityStatusKey.noLongerAvailable]: stockStatuses?.notLonger,
    [AvailabilityStatusKey.sizeNotSelected]: sizeIsNeededForStockStatusTextField,
    [SalesChannelKey.notSoldInStore]: stockStatuses?.notSoldInStore,
  };

  const isShoppingListLayout = layout === SHOPPING_LIST_LAYOUT;

  const isShoppingListById = useVariable<boolean>(Variables.SHOPPING_LIST_BY_ID);
  const shoppingListId = useVariable<string>(Variables.SHOPPING_LIST_ID);

  const isOwnerSharedShoppingList = shoppingListId === userShoppingListId;
  const currentStoreId = useMemo(
    () => getSelectedOrPreferredStoreId(authContext?.account),
    [authContext?.account],
  );

  const {
    name,
    masterPrice,
    rawPrice,
    rawClearancePrice,
    coloursNumber,
    masterImage,
    slug,
    variants,
    sku,
    displayColor,
    id: productId,
    displaySize,
    sizeId,
    endDate,
    startDate,
    inventory,
    comingSoon,
  } = product ?? {};

  const {
    clickAndCollectStore = null,
    clickAndCollectStock = null,
    clickAndCollectAvailableQuantity = null,
    salesChannel = null,
  } = inventory ?? DEFAULT_INVENTORY;

  const { id: widgetId, rid, type } = widgetData ?? {};

  const { isLg: isDesktop, isXl: isXlDesktop } = useBreakpoints();
  const isMobile = !isDesktop || !isXlDesktop;
  const notificationContext = useContext(NotificationContext);

  const statusKeyForInStoreStatus = useMemo(() => {
    if (salesChannel === SalesChannelKey.notSoldInStore) {
      return SalesChannelKey.notSoldInStore;
    } else {
      const statusKey =
        inventory?.available ?? AvailabilityStatusKey.notAvailableInStore;
      return statusKey;
    }
  }, [inventory, salesChannel]);

  const [currentSku, setCurrentSku] = useState<string>("");

  const { isComingSoon, isUnpublished } = useMemo(() => {
    if (!isShoppingListLayout) {
      return { isComingSoon: comingSoon, isUnpublished: false };
    }

    const nowDate = new Date().getTime();

    const isValidEndDate = endDate && new Date(endDate).getTime() >= nowDate;
    const isValidStartDate = startDate && new Date(startDate).getTime() <= nowDate;

    if (comingSoon && !startDate && (!endDate || isValidEndDate)) {
      return { isComingSoon: true, isUnpublished: false };
    }

    return {
      isComingSoon: false,
      isUnpublished: !isValidEndDate || !isValidStartDate,
    };
  }, [isShoppingListLayout, endDate, startDate, comingSoon]);

  const isOneSize = useMemo(() => oneSizeCodesSet.has(sizeId ?? ""), [sizeId]);

  const imgFallbacks = useMemo(() => {
    return (
      variants?.reduce<string[]>((urls, variant) => {
        if (!variant) {
          return urls;
        }
        // Product schema in PLP and Shopping List differs, but types suggest it's always ShoppingList
        const imagesKey = "images" in variant ? "images" : "productImages";

        (variant as ExtendedVariant)[imagesKey]?.forEach((image) => {
          if (image?.url) {
            urls.push(image?.url);
          }
        });
        return urls;
      }, []) || []
    );
  }, [variants]);

  const currentProductInShoppingBag = shoppingBagSkuMap?.get(product?.sku ?? "");

  const quantityLimit =
    storeSelectorData?.currentStore?.storeLimits?.maxLineItemQuantity;

  const isQuantityLimit =
    currentProductInShoppingBag?.quantity === Number(quantityLimit);

  const [isQuantityError, setIsQuantityError] = useState<boolean>(false);

  const analyticsData = useMemo(() => {
    return {
      account: authContext?.account,
      brand: dictionaryLabel?.[DictionaryKeys.BrandName],
      pageLocale: locale,
      position,
      product,
      store: storeSelectorData?.currentStore,
      statusKeyForInStoreStatus,
      variant,
    };
  }, [
    authContext,
    dictionaryLabel,
    locale,
    position,
    product,
    statusKeyForInStoreStatus,
    storeSelectorData?.currentStore,
    variant,
  ]);

  const addProductToShoppingBag = useCallback(
    async (e: React.BaseSyntheticEvent) => {
      e.preventDefault();
      const currency = getCurrencyCodeByLocale(locale);
      const currentDisplayPrice = isClearancePriceEnabled
        ? getDisplayPrice(locale, currency, rawClearancePrice || rawPrice, true)
        : masterPrice;

      const currentPrice = getDisplayPriceWithoutCurrency(currentDisplayPrice);

      const movedProduct = {
        shoppingListId: userShoppingListId,
        removableItemId: productId,
        shoppingBagId: userShoppingBag?.id || "",
        sku: sku,
        storeId: currentStoreId,
        sizeIsSelected,
      };
      if (isQuantityLimit) {
        setIsQuantityError(true);
        return;
      }
      if (!isQuantityLimit) {
        Evergage.sendEvent(locale, {
          itemAction: EvergageItemActions.ADD_TO_CART,
          cart: {
            singleLine: {
              Product: {
                _id: product?.productCode,
                quantity: 1,
                price: currentPrice,
              },
            },
          },
        });
        Evergage.sendEvent(locale, {
          action: EvergageActions.REMOVE_FROM_SHOPPING_LIST,
          itemAction: EvergageItemActions.FAVORITE,
          catalog: {
            Product: {
              _id: product?.productCode,
              name: product?.name,
              color: product?.displayColor,
            },
          },
        });

        const dataForGAAddToBagEvent = getDataForGAAddToBag(
          analyticsData,
          isClearancePriceEnabled,
        );
        pushDataLayer(dataForGAAddToBagEvent);

        handleMoveItemFromShoppingListToShoppingBag?.(movedProduct).then(
          (result) => {
            if (result?.data?.moveItemFromShoppingListToShoppingBag?.shoppingBag) {
              handleSetUserShoppingBag?.(
                result.data.moveItemFromShoppingListToShoppingBag.shoppingBag,
              );

              notificationContext?.handleShowNotification?.(
                moveItemToShoppingBagNotification,
                {
                  icon: <CcWhite24 />,
                },
              );
            }
          },
        );

        triggerAddToCartDYEvent({
          sku: product?.sku ?? null,
          currency: getCurrencyCodeByLocale(locale),
          price: Number(currentPrice),
          size: product?.displaySize ?? "",
          quantity: 1,
          existingProducts: userShoppingBag?.products ?? [],
        });
      }
    },
    [
      locale,
      isClearancePriceEnabled,
      rawClearancePrice,
      rawPrice,
      masterPrice,
      userShoppingListId,
      productId,
      userShoppingBag?.id,
      userShoppingBag?.products,
      sku,
      currentStoreId,
      sizeIsSelected,
      isQuantityLimit,
      product?.productCode,
      product?.name,
      product?.displayColor,
      product?.sku,
      product?.displaySize,
      analyticsData,
      handleMoveItemFromShoppingListToShoppingBag,
      handleSetUserShoppingBag,
      notificationContext,
    ],
  );

  const displaySizeShoppingListById = useMemo(() => {
    if (sizeIsSelected) {
      return displaySize;
    } else if (isOneSize) {
      return displaySize;
    } else {
      return sharedShoppingList?.notSelectedText;
    }
  }, [displaySize, isOneSize, sizeIsSelected, sharedShoppingList?.notSelectedText]);

  useEffect(() => {
    if (sizeIsSelected || isOneSize) {
      setCurrentSku(sku ?? "");
    } else {
      setCurrentSku("");
    }
  }, [isOneSize, sizeIsSelected, sku]);

  const isShoppingListEnabled = useMemo(
    () => config?.featureFlags?.shoppingList?.enabled,
    [config],
  );

  const isProductRemovedFromShoppingList = useMemo(() => {
    return deletedProducts?.filter(
      (deletedProduct) =>
        (deletedProduct.sku === currentSku || deletedProduct.sku === product?.sku) &&
        deletedProduct.sizeIsSelected === sizeIsSelected,
    )?.length;
  }, [currentSku, deletedProducts, product?.sku, sizeIsSelected]);

  const displayTags = useMemo(
    () =>
      productTags?.map(({ key, title, textColor, backgroundColor }) => {
        return (
          <Tag
            key={key}
            type={key}
            title={title}
            textColor={textColor}
            backgroundColor={backgroundColor}
          />
        );
      }),
    [productTags],
  );

  const displayPrice = useMemo(
    () =>
      sizeIsSelected
        ? masterPrice
        : getDisplayPriceFromVariants({
            variants: variants as ProductVariant[],
            masterVariant: {
              price: masterPrice ?? "",
              isActive: product?.isProductActive ?? undefined,
            },
            locale,
            currencyCode: getCurrencyCodeByLocale(locale),
            fromLabel: dictionaryLabel?.[DictionaryKeys.FromLabel],
          }),
    [
      sizeIsSelected,
      masterPrice,
      variants,
      product?.isProductActive,
      locale,
      dictionaryLabel,
    ],
  );

  const displayPriceWithClearance = useMemo(() => {
    if (isClearancePriceEnabled && rawPrice) {
      return sizeIsSelected
        ? {
            regular: rawPrice,
            clearance: rawClearancePrice,
          }
        : getDisplayPriceWithClearanceFromVariants({
            masterVariant: product ?? {},
            variants: variants as ProductVariant[],
          });
    }
  }, [
    isClearancePriceEnabled,
    sizeIsSelected,
    rawPrice,
    rawClearancePrice,
    product,
    variants,
  ]);

  const pricePerUnitText = useProductPricePerUnitText({
    measureOfPrice: product?.measureOfPrice as number,
    uomOfPrice: product?.uomOfPrice ?? "",
    pricePerUnit: product?.pricePerUnit as number,
  });

  const productLink = useMemo(() => {
    if (isUnpublished || !slug) {
      return null;
    }
    return getProductLinkBySlug(locale, slug);
  }, [isUnpublished, slug, locale]);

  const isStoreTrial = storeSelectorData?.currentStore?.trial;

  const clickAndCollectStatusKey = useMemo(() => {
    if (isStoreTrial && !authContext?.isMemberOfTrialStoreGroup) {
      return clickAndCollectAvailabilityStatusKey.notAvailableInStore;
    }
    if (salesChannel === SalesChannelKey.storeOnly) {
      return "";
    }
    if (
      clickAndCollectStore &&
      clickAndCollectStock &&
      clickAndCollectAvailableQuantity &&
      clickAndCollectAvailableQuantity >= 1
    ) {
      return clickAndCollectAvailabilityStatusKey.availableInStore;
    }

    if (
      clickAndCollectStore &&
      clickAndCollectStock &&
      clickAndCollectAvailableQuantity &&
      clickAndCollectAvailableQuantity < 1
    ) {
      return clickAndCollectAvailabilityStatusKey.soldOut;
    }

    if (!clickAndCollectStore && clickAndCollectStock) {
      return clickAndCollectAvailabilityStatusKey.notAvailableInStore;
    }
  }, [
    isStoreTrial,
    authContext,
    salesChannel,
    clickAndCollectStore,
    clickAndCollectStock,
    clickAndCollectAvailableQuantity,
  ]);

  const toShowAddToBagButton = useMemo<boolean>(() => {
    if (isStoreTrial && !authContext?.isMemberOfTrialStoreGroup) {
      return false;
    }
    return !!(
      !isShoppingListById &&
      isClickAndCollectEnabled &&
      isStoreSelected &&
      (sizeIsSelected || isOneSize) &&
      clickAndCollectStatusKey &&
      clickAndCollectStatuses[clickAndCollectStatusKey] ===
        clickAndCollectStatuses[
          clickAndCollectAvailabilityStatusKey.availableInStore
        ]
    );
  }, [
    clickAndCollectStatusKey,
    isClickAndCollectEnabled,
    authContext,
    isOneSize,
    isShoppingListById,
    isStoreSelected,
    isStoreTrial,
    sizeIsSelected,
  ]);

  const handleBloomrichWidgetClick = useCallback(
    (itemId: string) => {
      const logEvent = rid && widgetId && type;
      if (logEvent) {
        logBloomreachEvent({
          group: BloomreachGroupType.widget,
          etype: BloomreachEventType.widgetClick,
          wrid: rid,
          wid: widgetId,
          wty: type,
          item_id: itemId,
        });
      }
    },
    [rid, type, widgetId],
  );

  const removeProductFromList = useCallback(async () => {
    await handleRemoveProductFromShoppingList?.({
      shoppingListId: userShoppingListId,
      removableItemId: productId,
      sizeIsSelected,
      sku: currentSku,
    }).then(() => {
      const productToBeDeleted = {
        sku: product?.sku,
        sizeIsSelected,
        addedAt: product?.addedAt,
        images: [masterImage ?? {}],
        name: product?.name,
      };
      handleAddProductToDeleted?.(productToBeDeleted);
      Evergage.sendEvent(locale, {
        action: EvergageActions.REMOVE_FROM_SHOPPING_LIST,
        itemAction: EvergageItemActions.FAVORITE,
        catalog: {
          Product: {
            _id: product?.productCode,
            name: product?.name,
            color: product?.displayColor,
          },
        },
      });
    });
    if (isMobile) {
      // eslint-disable-next-line no-restricted-globals
      window?.scrollTo({
        top: removedProductRef?.current?.offsetTop,
        behavior: "smooth",
      });
    }
  }, [
    handleRemoveProductFromShoppingList,
    userShoppingListId,
    productId,
    sizeIsSelected,
    currentSku,
    isMobile,
    handleAddProductToDeleted,
    product?.sku,
    product?.addedAt,
    product?.name,
    product?.productCode,
    product?.displayColor,
    masterImage,
    locale,
  ]);

  const handleRemoveIconClick = useCallback(
    (e: React.BaseSyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      removeProductFromList();
    },
    [removeProductFromList],
  );

  const handleRemoveIconKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === Keys.Enter) {
        e.preventDefault();
        e.stopPropagation();
        removeProductFromList();
      }
    },
    [removeProductFromList],
  );

  const undoRemoveProductFromList = useCallback(() => {
    handleUndoRemoveProductFromShopingList?.({
      sku: product?.sku,
      sizeIsSelected,
      shoppingListId: userShoppingListId,
      addedAt: product?.addedAt,
    })
      .then(() => {
        if (product?.sku) {
          handleRemoveProductFromDeleted?.(product?.sku);
        }
        if (isMobile) {
          productRef?.current?.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
        Evergage.sendEvent(locale, {
          action: EvergageActions.ADD_TO_SHOPPING_LIST,
          itemAction: EvergageItemActions.FAVORITE,
          catalog: {
            Product: {
              _id: product?.productCode,
              name: product?.name,
              color: product?.displayColor,
            },
          },
        });
      })
      .catch(() => {
        return;
      });
  }, [
    handleUndoRemoveProductFromShopingList,
    handleRemoveProductFromDeleted,
    product?.addedAt,
    product?.sku,
    product?.productCode,
    product?.displayColor,
    product?.name,
    sizeIsSelected,
    userShoppingListId,
    isMobile,
    locale,
  ]);

  const handleUndoClick = useCallback(() => {
    undoRemoveProductFromList();
  }, [undoRemoveProductFromList]);

  const handleUndoKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === Keys.Enter) {
        e.preventDefault();
        e.stopPropagation();
        undoRemoveProductFromList();
      }
    },
    [undoRemoveProductFromList],
  );

  const handleChangeSize = useCallback(
    (sku: string) => {
      setIsQuantityError(false);
      handleReplaceShoppingListItem?.({
        shoppingListId: userShoppingListId,
        sku,
        removableItemId: productId,
      });
    },
    [handleReplaceShoppingListItem, productId, userShoppingListId],
  );

  const handleClick = useCallback(() => {
    setIndexToLocalStorage(productToPageMap, product?.productCode);

    const dataForGAClicksEvent = getDataForGAClicks(analyticsData, breadcrumbs);

    onClick?.();
    handleBloomrichWidgetClick(product?.productCode ?? "");
    pushDataLayer(dataForGAClicksEvent);

    const cleanPathname = pathname.replace(`/${locale}`, "");

    persistScrollPosition(product?.productCode ?? "", cleanPathname);
  }, [
    productToPageMap,
    product,
    analyticsData,
    breadcrumbs,
    onClick,
    handleBloomrichWidgetClick,
    pathname,
    locale,
  ]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }
      if (productLink && event.key === Keys.Enter) {
        handleClick();
        router.push(productLink);
      }
    },
    [handleClick, productLink, router],
  );

  useEffect(() => {
    if (!restorationRef) {
      return;
    }

    restorationRef?.current?.scrollIntoView({
      behavior: "auto",
      block: "center",
    });
  }, [restorationRef]);

  const isClickAndCollectStatus =
    isClickAndCollectEnabled &&
    isStoreSelected &&
    !!currentSku &&
    clickAndCollectStatusKey &&
    clickAndCollectStock;

  const { shouldChangeCCAndStoreStockStatusOrder } =
    useSwapCCAndStoreDyCampaignData();
  const { showNewIcon } = usePdpStockAvailsCampaign();
  const statusKeyForInStoreStatusIconColor =
    showNewIcon &&
    AvailabilityStatusData[statusKeyForInStoreStatus]?.iconColorVariation
      ? AvailabilityStatusData[statusKeyForInStoreStatus]?.iconColorVariation
      : AvailabilityStatusData[statusKeyForInStoreStatus]?.iconColor;

  const statusKeyForInStoreStatusIcon =
    showNewIcon && AvailabilityStatusData[statusKeyForInStoreStatus]?.iconVariation
      ? AvailabilityStatusData[statusKeyForInStoreStatus]?.iconVariation
      : AvailabilityStatusData[statusKeyForInStoreStatus]?.icon;

  let clickAndCollectIcon = null;
  let clickAndCollectIconColor = "";

  if (isClickAndCollectStatus) {
    const statusData = clickAndCollectStatusData[clickAndCollectStatusKey];

    if (showNewIcon && statusData?.iconVariation) {
      clickAndCollectIcon = statusData.iconVariation;
    } else {
      clickAndCollectIcon = statusData?.icon;
    }

    if (showNewIcon && statusData?.iconColorVariation) {
      clickAndCollectIconColor = statusData.iconColorVariation;
    } else {
      clickAndCollectIconColor = statusData?.iconColor || "";
    }
  }

  if (isProductRemovedFromShoppingList) {
    return (
      <RemovedProductCardContainer>
        <Box
          className={REMOVED_PRODUCT_CARD_CLASSES.removedProduct}
          {...getTestAutomationProps("product-card")}
          {...{ ref: removedProductRef }}
        >
          <Image
            alt={prepareTextAttribute(name, true)}
            width="100%"
            height="100%"
            className={REMOVED_PRODUCT_CARD_CLASSES.removedProductImage}
            src={masterImage?.url ? masterImage?.url : "/noImage.png"}
            imageTemplates={imageTemplates}
            fallbacks={imgFallbacks}
            loading="lazy"
          />
        </Box>
        <Box className={REMOVED_PRODUCT_CARD_CLASSES.removedProductDescription}>
          <Box>
            <Typography
              className={REMOVED_PRODUCT_CARD_CLASSES.removedProductName}
              variant="body2"
            >
              {name}
            </Typography>
            <Typography variant="body2" {...getTestAutomationProps("removed-label")}>
              {theProductHasBeenRemovedTextField}
            </Typography>
          </Box>
          <CombinedLink
            className={REMOVED_PRODUCT_CARD_CLASSES.undoButton}
            onClick={handleUndoClick}
            onKeyDown={handleUndoKeyDown}
            tabIndex={0}
          >
            {undoButtonName}
          </CombinedLink>
        </Box>
      </RemovedProductCardContainer>
    );
  }

  const colourLabelText =
    coloursNumber === 1
      ? dictionaryLabel?.[DictionaryKeys.Colour] ?? "color"
      : dictionaryLabel?.[DictionaryKeys.Colours] ?? "colors";

  const evergageProductData = {
    _id: product?.productCode,
    name: product?.name,
    color: product?.displayColor,
  };

  const googleAnalyticsData = {
    breadcrumbs,
    product: product ?? undefined,
    position,
    categorySlug: getMainCategorySlugFromProductData(product ?? undefined),
  };

  return (
    <ProductCardContainer
      className={className}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      isShoppingListLayout={isShoppingListLayout}
    >
      <ProductCardLink
        href={productLink ?? ""}
        onClick={handleClick}
        ref={restorationRef}
        tabIndex={-1}
        pageNumber={pageNumber}
      >
        <Box
          className={clsx({
            [PRODUCT_CARD_CLASSES.imageWrapper]: true,
            [PRODUCT_CARD_CLASSES.unpublishedProduct]: isUnpublished,
          })}
          {...{ ref: productRef }}
          {...getTestAutomationProps("product-card")}
        >
          {comingSoonTag && isComingSoon && !isUnpublished && (
            <Box className={clsx(PRODUCT_CARD_CLASSES.comingSoonTag)}>
              <Tag
                key={comingSoonTag.key}
                type={comingSoonTag.key}
                title={comingSoonTag.title}
                textColor={comingSoonTag.textColor}
                backgroundColor={comingSoonTag.backgroundColor}
              />
            </Box>
          )}
          <Image
            onKeyDown={handleKeyDown}
            alt={prepareTextAttribute(name, true)}
            width="100%"
            height="100%"
            className={PRODUCT_CARD_CLASSES.image}
            src={masterImage?.url ?? ""}
            imageTemplates={imageTemplates}
            fallbacks={imgFallbacks}
            loading="lazy"
            tabIndex={0}
          />
          <Box className={PRODUCT_CARD_CLASSES.tags}>{displayTags}</Box>
        </Box>
      </ProductCardLink>

      <Box className={PRODUCT_CARD_CLASSES.description}>
        <Box>
          {isUnpublished ? (
            <Typography
              component="span"
              variant="body2"
              className={PRODUCT_CARD_CLASSES.title}
              {...getTestAutomationProps("name")}
            >
              <ProductCardLink
                href={productLink ?? ""}
                onClick={handleClick}
                pageNumber={pageNumber}
              >
                {name}
              </ProductCardLink>
            </Typography>
          ) : (
            <>
              <Typography
                variant="body2"
                component="h2"
                className={PRODUCT_CARD_CLASSES.title}
                {...getTestAutomationProps("name")}
              >
                <ProductCardLink
                  href={productLink ?? ""}
                  onClick={handleClick}
                  pageNumber={pageNumber}
                >
                  {name}
                </ProductCardLink>
              </Typography>
              <Typography
                component="div"
                className={clsx(PRODUCT_CARD_CLASSES.price, "price")}
                {...getTestAutomationProps("price")}
              >
                <ProductCardLink
                  className={PRODUCT_CARD_CLASSES.priceLink}
                  href={productLink ?? ""}
                  onClick={handleClick}
                  pageNumber={pageNumber}
                >
                  {isClearancePriceEnabled && rawPrice ? (
                    <PricePanel price={displayPriceWithClearance} />
                  ) : (
                    <>{displayPrice}</>
                  )}
                  {isPricePerUnitFeatureEnabled && (
                    <Typography
                      className={PRODUCT_CARD_CLASSES.pricePerUnitText}
                      variant="body2"
                      component="span"
                    >
                      {pricePerUnitText}
                    </Typography>
                  )}
                </ProductCardLink>
              </Typography>

              {!!coloursNumber && !isShoppingListLayout && (
                <Typography
                  variant="body2"
                  className={PRODUCT_CARD_CLASSES.colors}
                  {...getTestAutomationProps("colors-number")}
                >
                  <ProductCardLink
                    href={productLink ?? ""}
                    onClick={handleClick}
                    pageNumber={pageNumber}
                  >
                    {`${coloursNumber} ${colourLabelText}`}
                  </ProductCardLink>
                </Typography>
              )}
            </>
          )}
        </Box>

        <Box
          className={clsx(
            PRODUCT_CARD_CLASSES.iconsGroup,
            isShoppingListLayout && PRODUCT_CARD_CLASSES.shoppingIconsGroup,
          )}
        >
          {comingSoonPhase2 && product?.comingSoon && !isRemindModal && (
            <RemindButton
              productId={product?.productCode ?? ""}
              productName={product?.name ?? ""}
              collectionName={product?.collectionName ?? ""}
              price={displayPrice}
              pricePerUnitText={pricePerUnitText}
              productTags={productTags}
              slug={product?.slug ?? ""}
              images={
                (variants?.[0] as { productImages: ProductImage[] })
                  ?.productImages || []
              }
              ProductCardComponent={
                ProductCard as React.ComponentType<ProductCardProps>
              }
            />
          )}
          {isShoppingListEnabled &&
            (!isShoppingListById || isOwnerSharedShoppingList) &&
            (isShoppingListLayout ? (
              <IconButton
                aria-label="trash button"
                {...getTestAutomationProps("trash-icon")}
                onClick={handleRemoveIconClick}
                onKeyDown={handleRemoveIconKeyDown}
                size="large"
              >
                <Trash />
              </IconButton>
            ) : (
              !isRecentlyViewed && (
                <HeartIcon
                  productSku={product?.sku ?? ""}
                  productName={name ?? ""}
                  evergageProductData={evergageProductData}
                  googleAnalyticsData={{
                    ...googleAnalyticsData,
                    priceWithClearance: displayPriceWithClearance,
                  }}
                  currentStore={storeSelectorData?.currentStore ?? undefined}
                />
              )
            ))}
        </Box>
      </Box>

      {isShoppingListLayout && (
        <>
          {isUnpublished ? (
            <Box className={PRODUCT_CARD_CLASSES.shoppingListProductStatus}>
              <Box
                className={PRODUCT_CARD_CLASSES.statusIcon}
                style={{
                  color: showNewIcon
                    ? AvailabilityStatusData[
                        AvailabilityStatusKey.notAvailableInStore
                      ].iconColorVariation
                    : AvailabilityStatusData[
                        AvailabilityStatusKey.notAvailableInStore
                      ].iconColor,
                }}
              >
                {showNewIcon
                  ? AvailabilityStatusData[AvailabilityStatusKey.notAvailableInStore]
                      .iconVariation
                  : AvailabilityStatusData[AvailabilityStatusKey.notAvailableInStore]
                      .icon}
              </Box>
              <Typography variant="body2">
                {statusConfig[AvailabilityStatusKey.noLongerAvailable]}
              </Typography>
            </Box>
          ) : (
            <>
              <Box {...getTestAutomationProps("color")}>
                <Typography
                  variant="body2"
                  className={PRODUCT_CARD_CLASSES.shoppingListProductColor}
                >
                  <ProductCardLink
                    href={productLink ?? ""}
                    onClick={handleClick}
                    pageNumber={pageNumber}
                  >
                    {startCase(dictionaryLabel?.[DictionaryKeys.Colour])}:&ensp;
                    <Typography
                      component="span"
                      variant="body2"
                      data-evergage-product-color={displayColor}
                    >
                      {displayColor}
                    </Typography>
                  </ProductCardLink>
                </Typography>
              </Box>
              {!isComingSoon && (
                <>
                  {isShoppingListById && !isOwnerSharedShoppingList ? (
                    <Typography
                      className={clsx(className, PRODUCT_CARD_CLASSES.labelRow)}
                      variant="body2"
                    >
                      {sharedShoppingList?.sizeText}: {displaySizeShoppingListById}
                    </Typography>
                  ) : isOneSize ? (
                    <Typography
                      variant="body1"
                      className={PRODUCT_CARD_CLASSES.oneSizeLabel}
                    >
                      {displaySize}
                    </Typography>
                  ) : (
                    <SizeSelector
                      className={clsx(
                        PRODUCT_CARD_CLASSES.block,
                        PRODUCT_CARD_CLASSES.sizeBox,
                      )}
                      shoppingListPlaceholderText={selectSizeTextField ?? ""}
                      isSizeErrorShown={false}
                      variants={variants as ProductVariant[]}
                      onChange={handleChangeSize}
                      isShoppingListLayout={isShoppingListLayout}
                      currentSize={sizeIsSelected ? sku : undefined}
                      id={productId ?? ""}
                    />
                  )}
                  {shouldChangeCCAndStoreStockStatusOrder &&
                    isClickAndCollectStatus && (
                      <Box
                        className={PRODUCT_CARD_CLASSES.shoppingListProductStatus}
                        {...getTestAutomationProps("clickAndCollect-status")}
                      >
                        <Box
                          className={PRODUCT_CARD_CLASSES.statusIcon}
                          style={{
                            color: clickAndCollectIconColor,
                          }}
                        >
                          {clickAndCollectIcon}
                        </Box>
                        <Typography variant="body2">
                          {clickAndCollectStatuses[clickAndCollectStatusKey]}
                        </Typography>
                      </Box>
                    )}
                  {isStoreSelected && !!currentSku && (
                    <Box
                      className={PRODUCT_CARD_CLASSES.shoppingListProductStatus}
                      {...getTestAutomationProps("stock-status")}
                    >
                      <Box
                        className={PRODUCT_CARD_CLASSES.statusIcon}
                        style={{
                          color: statusKeyForInStoreStatusIconColor,
                        }}
                      >
                        {statusKeyForInStoreStatusIcon}
                      </Box>
                      <Typography variant="body2">
                        {statusConfig?.[statusKeyForInStoreStatus]}
                      </Typography>
                    </Box>
                  )}
                  {isStoreSelected && !currentSku && (
                    <Box className={PRODUCT_CARD_CLASSES.shoppingListProductStatus}>
                      <Box className={PRODUCT_CARD_CLASSES.statusIcon}>
                        {
                          AvailabilityStatusData[
                            AvailabilityStatusKey.sizeNotSelected
                          ]?.icon
                        }
                      </Box>
                      <Typography variant="body2">
                        {statusConfig[AvailabilityStatusKey.sizeNotSelected]}
                      </Typography>
                    </Box>
                  )}
                  {!shouldChangeCCAndStoreStockStatusOrder &&
                    isClickAndCollectStatus && (
                      <Box
                        className={PRODUCT_CARD_CLASSES.shoppingListProductStatus}
                        {...getTestAutomationProps("clickAndCollect-status")}
                      >
                        <Box
                          className={PRODUCT_CARD_CLASSES.statusIcon}
                          style={{
                            color: clickAndCollectIconColor,
                          }}
                        >
                          {clickAndCollectIcon}
                        </Box>
                        <Typography variant="body2">
                          {clickAndCollectStatuses[clickAndCollectStatusKey]}
                        </Typography>
                      </Box>
                    )}
                  {toShowAddToBagButton && (
                    <Button
                      variant="outlined"
                      className={PRODUCT_CARD_CLASSES.addToBag}
                      onClick={
                        !isQuantityError
                          ? addProductToShoppingBag
                          : (e) => e.preventDefault()
                      }
                    >
                      {moveToShoppingBagText}
                    </Button>
                  )}
                  {isQuantityError && (
                    <Typography
                      variant="body1"
                      className={PRODUCT_CARD_CLASSES.maxQuantityError}
                    >
                      {shoppingBagLimitErrorText}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </ProductCardContainer>
  );
};
